import {filter, find, isEmpty, map} from 'lodash';
import {useCallback, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Card, CardRow, DataWrapper, EmailLink, Table, TableBody, TableCell, TableRow, UserInitials} from 'components';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {getUserGroupsNames} from 'services';
import {User, UserRole, UsersAvailabilityData} from 'types';
import {getParticipantName, getRolesNames} from 'utils/transform';

import {UsersAvailabilityMetric, UsersAvailabilityMetricConfig} from '../types';

const COLUMNS_T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.USERS.CURRENT_USERS.TABLE.COLUMNS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON.ALL;

type Props = {
    metric: UsersAvailabilityMetric;
    groups: Group[];
    roles: UserRole[];
    usersAvailability: UsersAvailabilityData;
}

export const MetricUsers: React.FC<Props> = ({metric, groups, roles, usersAvailability}) => {
    const {t} = useTranslation();

    const isMetricUser = useCallback((user: User) => {
        switch (metric) {
            case UsersAvailabilityMetric.InSession:
                return usersAvailability.onsiteUsersEids.includes(user.eid) &&
                    usersAvailability.inSessionUsersEids.includes(user.eid) && user.isAvailable;
            case UsersAvailabilityMetric.NotInSession:
                return usersAvailability.onsiteUsersEids.includes(user.eid) &&
                    !usersAvailability.inSessionUsersEids.includes(user.eid) && user.isAvailable;
            case UsersAvailabilityMetric.Online:
                return usersAvailability.onsiteUsersEids.includes(user.eid) && user.isAvailable;
            case UsersAvailabilityMetric.Offline:
                return !usersAvailability.onsiteUsersEids.includes(user.eid);
            case UsersAvailabilityMetric.Away:
                return usersAvailability.onsiteUsersEids.includes(user.eid) && !user.isAvailable;
        }
    }, [metric, usersAvailability]);

    const users = useMemo(() => filter(usersAvailability.users, isMetricUser), [usersAvailability, isMetricUser]);

    const showInSessionInfo = useMemo(() =>
        [UsersAvailabilityMetric.Online, UsersAvailabilityMetric.Away, UsersAvailabilityMetric.Offline]
            .includes(metric), [metric]);

    const columns = useMemo(() => [
        t(COLUMNS_T_KEYS.NAME),
        t(COLUMNS_T_KEYS.ASSIGNMENT),
        t(COLUMNS_T_KEYS.EMAIL),
        t(COLUMNS_T_KEYS.ROLES),
        ...(showInSessionInfo ? [t(COMMON_T_KEYS.IN_SESSION)] : [])],
    [t, showInSessionInfo]);
    const total = useMemo(() => UsersAvailabilityMetricConfig[metric].valueGetter(usersAvailability),
        [metric, usersAvailability]);

    const metricConfig = useMemo(() => find(UsersAvailabilityMetricConfig, (config) => config.metric === metric), [metric]);

    const isInSession = useCallback((user: User) => usersAvailability?.inSessionUsersEids.includes(user.eid), [usersAvailability]);

    return (
        <div className='tw-mt-8'>
            <div className='tw-text-lg md:tw-text-3xl md:tw-text-bold tw-mb-4'>
                {t(metricConfig.title)}
                <span className='tw-ml-1'>{`(${total})`}</span>
            </div>
            <Card className='-md:tw-hidden'>
                <Table headerNames={columns} noData={isEmpty(users)}>
                    <TableBody>
                        {map(users, (user, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <div className='tw-flex tw-items-center tw-text-break-word'>
                                        <UserInitials
                                            user={user}
                                            className='tw-mr-2'
                                            showUnactivated
                                        />
                                        {getParticipantName(user)}
                                    </div>
                                </TableCell>
                                <TableCell>{getUserGroupsNames(user.groups, groups)}</TableCell>
                                <TableCell>{user.email}</TableCell>
                                <TableCell>{getRolesNames(t, user.roles, roles)}</TableCell>
                                {showInSessionInfo &&
                                    <TableCell>
                                        {t(isInSession(user) ? COMMON_T_KEYS.YES : COMMON_T_KEYS.NO)}
                                    </TableCell>}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            <div className='md:tw-hidden'>
                <DataWrapper noData={isEmpty(users)}>
                    {map(users, (user, index) => {
                        const isUserInSession = isInSession(user);
                        const userRoles = getRolesNames(t, user.roles, roles);
                        const userGroupsNames = getUserGroupsNames(user.groups, groups);
                        return (
                            <Card key={index} className='tw-mb-6'>
                                <div className='tw-flex tw-items-center tw-font-bold'>
                                    <UserInitials
                                        user={user}
                                        className='tw-mr-2'
                                        showUnactivated
                                    />
                                    {getParticipantName(user)}
                                </div>
                                {!isEmpty(userGroupsNames) && <CardRow icon='assignment'>{userGroupsNames}</CardRow>}
                                <CardRow icon='mail'>
                                    <EmailLink email={user.email} />
                                </CardRow>
                                {!isEmpty(userRoles) && <CardRow icon='permission'>{userRoles}</CardRow>}
                                {showInSessionInfo &&
                                    <CardRow icon={isUserInSession ? 'logo' : 'logo-crossed'}>
                                        {t(isUserInSession ? COMMON_T_KEYS.IN_SESSION : COMMON_T_KEYS.NOT_IN_SESSION)}
                                    </CardRow>}
                            </Card>
                        );
                    })}
                </DataWrapper>

            </div>
        </div>
    );
};